/**
 * オフライン回答キュー。design §4.5, §4.6
 */
import { apiPost } from "../api/client";
import { getOfflineDB, PendingAnswer } from "./db";

let isSyncing = false;

export async function enqueuePendingAnswer(
  answer: Omit<PendingAnswer, "id">,
): Promise<void> {
  const db = await getOfflineDB();
  await db.add("pendingAnswers", answer as PendingAnswer);
}

export async function getPendingAnswers(): Promise<PendingAnswer[]> {
  const db = await getOfflineDB();
  return db.getAll("pendingAnswers");
}

export async function getPendingAnswerCount(): Promise<number> {
  const db = await getOfflineDB();
  return db.count("pendingAnswers");
}

export interface SyncResult {
  syncedCount: number;
  remainingCount: number;
}

/**
 * 未送信の回答を1件ずつ順番に POST /answers する。
 * 成功したら削除して次へ、失敗したら中断する（残りは次回の同期に委ねる）。
 * 多重実行防止のフラグを持つ（既に同期中の呼び出しは即座に現在の残数を返す）。
 */
export async function syncPendingAnswers(
  idToken?: string,
): Promise<SyncResult> {
  if (isSyncing) {
    return { syncedCount: 0, remainingCount: await getPendingAnswerCount() };
  }

  isSyncing = true;
  try {
    const db = await getOfflineDB();
    let syncedCount = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const pending = await db.getAll("pendingAnswers");
      if (pending.length === 0) break;

      const next = pending[0];
      try {
        await apiPost(
          "/answers",
          {
            questionId: next.questionId,
            selectedAnswer: next.selectedAnswer,
          },
          idToken,
        );
        if (next.id !== undefined) {
          await db.delete("pendingAnswers", next.id);
        }
        syncedCount += 1;
      } catch {
        // ネットワーク/サーバエラー: 中断し次回の同期トリガに委ねる
        break;
      }
    }

    return { syncedCount, remainingCount: await db.count("pendingAnswers") };
  } finally {
    isSyncing = false;
  }
}
